import posthog from 'posthog-js';
import { useAuth, useOrganization, useUser } from '@clerk/clerk-react';
import { useEffect } from 'react';

// Initialize PostHog in production or localhost mode for testing
const isProduction = process.env.NODE_ENV === 'production';
const isLocalhost = import.meta.env.MODE === 'localhost';
const shouldInitialize = isProduction || isLocalhost;
const posthogKey = process.env.REACT_POSTHOG_KEY;
const posthogHost = process.env.REACT_POSTHOG_HOST;

if (shouldInitialize && posthogKey && posthogHost) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    person_profiles: 'always', // Since users agreed to T&C, track all profiles
    capture_pageview: false, // We'll handle this manually for better control
    capture_pageleave: true,
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development') posthog.debug();
    },
  });
}

export const usePostHog = () => {
  const { isSignedIn, userId } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();

  useEffect(() => {
    if (!shouldInitialize || !posthogKey) return;

    if (isSignedIn && userId && user) {
      // Identify the user with Clerk data
      posthog.identify(userId, {
        email: user.primaryEmailAddress?.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        // Add company/organization context if available
        ...(organization && {
          organizationId: organization.id,
          organizationName: organization.name,
          organizationSlug: organization.slug,
        }),
        // Add any custom user metadata
        createdAt: user.createdAt,
        lastSignInAt: user.lastSignInAt,
      });

      // Set organization context if available
      if (organization) {
        posthog.group('organization', organization.id, {
          name: organization.name,
          slug: organization.slug,
          createdAt: organization.createdAt,
          membersCount: organization.membersCount,
        });
      }
    }
  }, [isSignedIn, userId, user, organization]);

  // Return PostHog instance for manual tracking
  return shouldInitialize ? posthog : null;
};

export { posthog };
